import {
  Typography,
  Button,
  Paper,
  TextField,
  IconButton,
} from "@mui/material";
import { Box, Grid } from "@mui/system";
import { Dispatch } from "react";
import { FaPlus, FaTrash } from "react-icons/fa";
import { errorActions } from "../../../store/errorSlice";
import { useAppDispatch } from "../../../store/hooks";
import {
  MAX_UPLOADED_TNS,
  ResidentialEntry,
  ResidentialEntryFormError,
} from "./NumberNameRequest";

interface ResidentialEntriesInterface {
  residentialEntries: ResidentialEntry[];
  setResidentialEntries: Dispatch<React.SetStateAction<ResidentialEntry[]>>;
  residentialEntriesErrors: ResidentialEntryFormError[];
  setResidentialEntriesErrors: Dispatch<
    React.SetStateAction<ResidentialEntryFormError[]>
  >;
}

export const ResidentialEntryForm = ({
  residentialEntries,
  setResidentialEntries,
  residentialEntriesErrors,
  setResidentialEntriesErrors,
}: ResidentialEntriesInterface) => {
  const dispatch = useAppDispatch();

  const addResidentialEntry = () => {
    if (residentialEntries.length >= 50) {
      dispatch(
        errorActions.setError({
          errorMessage: "Maximum 50 person entries allowed",
        })
      );
      return;
    }
    setResidentialEntries([
      ...residentialEntries,
      {
        id: Date.now().toString(),
        phoneNumber: "",
        currentCnam: "",
        desiredCnam: "",
        comment: "",
      },
    ]);
  };

  const removeResidentialEntry = (id: string) => {
    if (residentialEntries.length === 1) return;
    setResidentialEntries(
      residentialEntries.filter((entry) => entry.id !== id)
    );
    setResidentialEntriesErrors(
      residentialEntriesErrors.filter((e) => e.id !== id)
    );
  };

  const updateResidentialEntry = (
    id: string,
    field: keyof ResidentialEntry,
    value: string
  ) => {
    setResidentialEntries(
      residentialEntries.map((entry) =>
        entry.id === id ? { ...entry, [field]: value } : entry
      )
    );

    // If they're updating a field with an error, clear that error from the error list
    setResidentialEntriesErrors(
      residentialEntriesErrors.filter((e) => e.id !== id || e.key !== field)
    );
  };

  return (
    <>
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h6">Residential Number Name (1:1)</Typography>
          <Button
            variant="contained"
            startIcon={<FaPlus />}
            onClick={addResidentialEntry}
            disabled={residentialEntries.length > MAX_UPLOADED_TNS}
          >
            Add Residential Entry
          </Button>
        </Box>

        {residentialEntries.map((entry) => {
          const errorState = residentialEntriesErrors.filter(
            (e) => e.id === entry.id
          );

          const errorStateKeys = errorState.map((e) => e.key);

          return (
            <Paper
              key={entry.id}
              sx={{ padding: 2, mb: 2, backgroundColor: "#f1f5f9" }}
            >
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={entry.phoneNumber}
                    onChange={(e) =>
                      updateResidentialEntry(
                        entry.id,
                        "phoneNumber",
                        e.target.value
                      )
                    }
                    error={
                      errorState !== undefined &&
                      errorStateKeys.includes("phoneNumber")
                    }
                    required
                    helperText={
                      errorStateKeys.includes("phoneNumber")
                        ? errorState.find((e) => e.key === "phoneNumber")?.error
                        : undefined
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    fullWidth
                    label="Expected Number Name"
                    value={entry.desiredCnam}
                    onChange={(e) =>
                      updateResidentialEntry(
                        entry.id,
                        "desiredCnam",
                        e.target.value
                      )
                    }
                    inputProps={{ maxLength: 15 }}
                    error={
                      errorState !== undefined &&
                      errorStateKeys.includes("desiredCnam")
                    }
                    helperText={
                      errorStateKeys.includes("desiredCnam")
                        ? errorState.find((e) => e.key === "desiredCnam")?.error
                        : undefined
                    }
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 2.5 }}>
                  <TextField
                    fullWidth
                    label="Current Number Name"
                    value={entry.currentCnam}
                    onChange={(e) =>
                      updateResidentialEntry(
                        entry.id,
                        "currentCnam",
                        e.target.value
                      )
                    }
                    error={
                      errorState !== undefined &&
                      errorStateKeys.includes("currentCnam")
                    }
                    helperText={
                      errorStateKeys.includes("currentCnam")
                        ? errorState.find((e) => e.key === "currentCnam")?.error
                        : undefined
                    }
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 2.5 }}>
                  <TextField
                    fullWidth
                    label="Comment"
                    value={entry.comment}
                    onChange={(e) =>
                      updateResidentialEntry(
                        entry.id,
                        "comment",
                        e.target.value
                      )
                    }
                    error={
                      errorState !== undefined &&
                      errorStateKeys.includes("comment")
                    }
                    helperText={
                      errorStateKeys.includes("comment")
                        ? errorState.find((e) => e.key === "comment")?.error
                        : undefined
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      height: "56px",
                    }}
                  >
                    <IconButton
                      color="error"
                      onClick={() => removeResidentialEntry(entry.id)}
                      disabled={residentialEntries.length === 1}
                    >
                      <FaTrash />
                    </IconButton>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          );
        })}
      </Box>
    </>
  );
};
